import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Download, Share2, Heart, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';

/**
 * PhotoLightbox Component - 2026 UX/UI Design
 * Features:
 * - Edge-to-edge display
 * - Auto-hide controls after 3s
 * - Pinch-to-zoom + pan support
 * - Swipe navigation with CSS transitions
 * - Thumbnail strip navigation
 */
export function PhotoLightbox({
  open,
  onClose,
  photos = [],
  initialIndex = 0,
  onDownload,
  onFavorite,
  onShare,
  favoritedPhotoIds = new Set(),
  shareToken,
  backendUrl,
  paywallEnabled = false,
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showControls, setShowControls] = useState(true);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  const controlsTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });

  const currentPhoto = photos[currentIndex];

  // Reset index when photos change
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  // Auto-hide controls after 3 seconds
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (!isDragging) {
        setShowControls(false);
      }
    }, 3000);
  }, [isDragging]);

  useEffect(() => {
    if (open) {
      resetControlsTimeout();
    }
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [open, resetControlsTimeout]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowLeft':
          navigatePrev();
          break;
        case 'ArrowRight':
          navigateNext();
          break;
        case 'Escape':
          onClose?.();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex, photos.length]);

  // Navigation
  const navigatePrev = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
    setImageKey((k) => k + 1);
    resetControlsTimeout();
  };

  const navigateNext = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setCurrentIndex((prev) => (prev + 1) % photos.length);
    setImageKey((k) => k + 1);
    resetControlsTimeout();
  };

  const navigateToIndex = (index) => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setCurrentIndex(index);
    setImageKey((k) => k + 1);
    resetControlsTimeout();
  };

  // Zoom controls
  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.5, 4));
    resetControlsTimeout();
  };

  const handleZoomOut = () => {
    setScale((prev) => {
      const newScale = Math.max(prev - 0.5, 1);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
    resetControlsTimeout();
  };

  const handleDoubleClick = () => {
    if (scale > 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(2);
    }
    resetControlsTimeout();
  };

  // Get photo URL
  const getPhotoUrl = (photo, type = 'preview') => {
    const url = type === 'thumbnail'
      ? (photo?.thumbnail_url || photo?.thumbnail_path)
      : (photo?.preview_url || photo?.preview_path);
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${backendUrl}/api/photos/file/${type === 'thumbnail' ? 'thumbnails' : 'previews'}/${url.split('/').pop()}`;
  };

  // Touch/swipe handlers
  const handleTouchStart = (e) => {
    if (scale === 1) {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      setIsDragging(true);
    }
  };

  const handleTouchEnd = (e) => {
    if (scale === 1 && isDragging) {
      const touchEnd = {
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY,
      };
      const diffX = touchEnd.x - touchStartRef.current.x;
      const diffY = touchEnd.y - touchStartRef.current.y;
      const threshold = 50;

      if (Math.abs(diffX) > threshold && Math.abs(diffX) > Math.abs(diffY)) {
        if (diffX > 0) {
          navigatePrev();
        } else {
          navigateNext();
        }
      } else if (diffY > threshold) {
        onClose?.();
      }
    }
    setIsDragging(false);
  };

  if (!currentPhoto) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="
          max-w-full w-screen h-screen max-h-screen
          p-0 bg-black/95 backdrop-blur-2xl border-0
          [&>button]:hidden overflow-hidden
        "
        aria-describedby={undefined}
      >
        {/* Visually hidden accessibility elements */}
        <DialogTitle className="sr-only">
          {currentPhoto?.filename || 'Photo viewer'}
        </DialogTitle>
        <div
          ref={containerRef}
          className="relative w-full h-full flex flex-col"
          onMouseMove={resetControlsTimeout}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Top Controls */}
          <div
            className={`
              absolute top-0 left-0 right-0 z-50
              bg-gradient-to-b from-black/70 to-transparent
              p-4 pt-safe
              transition-all duration-300
              ${showControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}
            `}
          >
            <div className="flex items-center justify-between">
              {/* Left Controls */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => onDownload?.(currentPhoto)}
                  className="bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-2xl"
                  size="icon"
                >
                  <Download className="w-5 h-5" />
                </Button>
                <Button
                  onClick={() => onShare?.(currentPhoto)}
                  className="bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-2xl"
                  size="icon"
                >
                  <Share2 className="w-5 h-5" />
                </Button>
              </div>

              {/* Right Controls */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => onFavorite?.(currentPhoto)}
                  className={`
                    rounded-2xl transition-all
                    ${favoritedPhotoIds.has(currentPhoto.id)
                      ? 'bg-gradient-to-r from-[#BF212F] to-[#F26B38] text-white'
                      : 'bg-white/10 backdrop-blur-xl text-white hover:bg-white/20'
                    }
                  `}
                  size="icon"
                >
                  <Heart
                    className={`w-5 h-5 ${favoritedPhotoIds.has(currentPhoto.id) ? 'fill-white' : ''}`}
                  />
                </Button>
                <Button
                  onClick={onClose}
                  className="bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-2xl"
                  size="icon"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Main Image - fills available space between header and footer */}
          <div
            className="absolute left-0 right-0 flex items-center justify-center"
            style={{
              top: '70px',
              bottom: '140px',
              padding: '8px'
            }}
          >
            <img
              key={imageKey}
              src={getPhotoUrl(currentPhoto, 'preview')}
              alt={currentPhoto.filename}
              className="select-none transition-transform duration-200 cursor-grab active:cursor-grabbing"
              style={{
                transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                ...(paywallEnabled ? { WebkitTouchCallout: 'none' } : {}),
              }}
              onDoubleClick={handleDoubleClick}
              onContextMenu={paywallEnabled ? (e) => e.preventDefault() : undefined}
              draggable={false}
            />
          </div>

          {/* Navigation Arrows */}
          {photos.length > 1 && (
            <>
              <button
                onClick={navigatePrev}
                className={`
                  absolute left-4 top-1/2 -translate-y-1/2 z-40
                  bg-white/10 backdrop-blur-xl text-white
                  hover:bg-white/20 rounded-2xl
                  w-12 h-12 sm:w-14 sm:h-14
                  flex items-center justify-center
                  transition-all duration-300
                  ${showControls ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none'}
                `}
              >
                <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
              <button
                onClick={navigateNext}
                className={`
                  absolute right-4 top-1/2 -translate-y-1/2 z-40
                  bg-white/10 backdrop-blur-xl text-white
                  hover:bg-white/20 rounded-2xl
                  w-12 h-12 sm:w-14 sm:h-14
                  flex items-center justify-center
                  transition-all duration-300
                  ${showControls ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'}
                `}
              >
                <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            </>
          )}

          {/* Bottom Controls & Thumbnail Strip */}
          <div
            className={`
              absolute bottom-0 left-0 right-0 z-50
              bg-gradient-to-t from-black/70 to-transparent
              p-4 pb-safe
              transition-all duration-300
              ${showControls ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
            `}
          >
            {/* Zoom Controls */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <Button
                onClick={handleZoomOut}
                disabled={scale <= 1}
                className="bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-xl h-8 w-8"
                size="icon"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-white text-sm font-medium min-w-[50px] text-center">
                {Math.round(scale * 100)}%
              </span>
              <Button
                onClick={handleZoomIn}
                disabled={scale >= 4}
                className="bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 rounded-xl h-8 w-8"
                size="icon"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>

            {/* Thumbnail Strip */}
            {photos.length > 1 && (
              <div className="flex justify-center gap-1 overflow-x-auto scrollbar-hide py-2">
                {photos.slice(Math.max(0, currentIndex - 3), currentIndex + 4).map((photo, idx) => {
                  const actualIndex = Math.max(0, currentIndex - 3) + idx;
                  const isActive = actualIndex === currentIndex;

                  return (
                    <button
                      key={photo.id}
                      onClick={() => navigateToIndex(actualIndex)}
                      className={`
                        flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden
                        transition-all duration-200
                        ${isActive
                          ? 'ring-2 ring-white scale-110'
                          : 'opacity-60 hover:opacity-100 hover:scale-105'
                        }
                      `}
                    >
                      <img
                        src={getPhotoUrl(photo, 'thumbnail')}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            )}

            {/* Filename */}
            <p className="text-center text-white/80 text-sm mt-2 truncate px-4">
              {currentPhoto.filename}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PhotoLightbox;
